import React, { useState } from "react";
import { Copy, Check } from "lucide-react";

/**
 * iter70d · Small click-to-copy identifier chip. Used anywhere we need to
 * expose a stable internal ID (member_id / vendor_id / user_id) to a
 * platform operator or the tenant themselves for support & debugging.
 *
 * The chip renders the ID in mono, copies the FULL value on click, and
 * flashes a check for 1.2s to confirm. Falls back to a plain span if the
 * clipboard API isn't available (older mobile browsers).
 */
export default function IdChip({
  value,
  label,
  size = "sm",
  testId,
}) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(value));
      } else {
        const ta = document.createElement("textarea");
        ta.value = String(value);
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard blocked → still show visual feedback */ }
  };
  const pad = size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]";
  return (
    <button
      type="button"
      onClick={copy}
      title={`Click to copy ${label || "ID"}: ${value}`}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 font-mono ${pad} bg-[#F3F1E9] border border-[#E5E3D8] text-[#4A4A4A] hover:border-[#1A422D] hover:text-[#1A422D] transition-colors`}
    >
      {label && (
        <span className="uppercase tracking-[0.16em] text-[9px] text-[#7A7A7A] font-sans">
          {label}
        </span>
      )}
      <span className="tracking-normal">{value}</span>
      {copied ? <Check size={11} className="text-[#1A422D]" /> : <Copy size={11} className="opacity-50" />}
    </button>
  );
}
